import React from 'react';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import ScrollToTop from '../components/ScrollToTop';
import SEO from '../components/SEO/SEO';

const AboutPage = () => {
    return (
        <div className="bg-background-light dark:bg-background-dark text-slate-700 dark:text-slate-300 font-body transition-colors duration-300 min-h-screen">
            <SEO
                title="About Us - Dr. Lakruwan Wijayasinghe"
                description="Learn about Dr. Lakruwan Wijayasinghe and the Smile Care Dental Surgery team in Kottawa. Expert dental care with a personal touch."
                url="/about-us"
                image="/images/dr-lukuwan-pro.png"
            />
            <ScrollToTop />
            <Navbar />

            {/* Header Section - Minimal */}
            <div className="pt-32 pb-16 bg-white dark:bg-slate-900 relative overflow-hidden">
                <div className="absolute top-[-10%] right-[-5%] w-96 h-96 rounded-full bg-primary/5 blur-3xl"></div>

                <div className="container mx-auto px-6 md:px-12 relative z-10 text-center">
                    <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs mb-4 block">
                        Get to Know Us
                    </span>
                    <h1 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6">
                        More Than Just a Clinic
                    </h1>
                    <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto font-light leading-relaxed">
                        At Smile Care, we believe that world-class dentistry starts with a conversation. Meet the team dedicated to your well-being.
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <section className="py-20">
                <div className="container mx-auto px-6 md:px-12">
                    {/* Doctor Profile - Refined Layout */}
                    <div className="flex flex-col lg:flex-row items-center gap-16 mb-24">
                        {/* Image Side */}
                        <div className="lg:w-1/2 relative group">
                            <div className="absolute inset-0 bg-primary/10 rounded-3xl transform rotate-3 transition-transform duration-500 group-hover:rotate-6"></div>
                            <div className="absolute inset-0 bg-secondary/10 rounded-3xl transform -rotate-2 transition-transform duration-500 group-hover:-rotate-4"></div>
                            <img
                                src="/images/dr-lukuwan-pro.png"
                                alt="Dr. Lakruwan Wijayasinghe"
                                className="rounded-2xl shadow-2xl w-full h-96 md:h-[550px] object-cover relative z-10 grayscale-[10%] group-hover:grayscale-0 transition-all duration-500"
                            />
                        </div>

                        {/* Text Side */}
                        <div className="lg:w-1/2">
                            <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-2">
                                Dr. Lakruwan Wijayasinghe
                            </h2>
                            <p className="text-primary font-bold uppercase tracking-wide text-xs mb-6">
                                Dental Surgeon (BDS - SL) | Implantologist (AAID - USA)
                            </p>

                            <ul className="space-y-4 mb-8 text-slate-600 dark:text-slate-400 font-light text-base">
                                <li className="flex items-start gap-3">
                                    <i className="fas fa-check-circle text-secondary mt-1 text-sm"></i>
                                    <span>Orthodontic General Practitioner (POS - USA)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <i className="fas fa-check-circle text-secondary mt-1 text-sm"></i>
                                    <span>Fellow of orthodontic in General Practice (ICCDE)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <i className="fas fa-check-circle text-secondary mt-1 text-sm"></i>
                                    <span>Associate Fellow of Laser Dentistry (WCLI - USA)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <i className="fas fa-check-circle text-secondary mt-1 text-sm"></i>
                                    <span>Fellow in Aesthetic Medicine and Cosmetology</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <i className="fas fa-id-card text-secondary mt-1 text-sm"></i>
                                    <span>SLMC Reg. No: 2446</span>
                                </li>
                            </ul>

                            <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-xl border border-slate-100 dark:border-slate-700/50 mb-8">
                                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-widest font-bold mb-2">Current Positions</p>
                                <p className="text-slate-800 dark:text-slate-200 font-semibold text-lg leading-tight">
                                    Owner - Smile Care Dental Surgery (VIP Center)
                                </p>
                                <p className="text-slate-600 dark:text-slate-400 font-medium mt-1">
                                    & FineDent Dental Laboratory
                                </p>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-2 gap-8 border-t border-slate-100 dark:border-slate-800 pt-8">
                                <div>
                                    <h4 className="font-display font-bold text-3xl text-slate-900 dark:text-white mb-1">15+</h4>
                                    <p className="text-xs text-slate-400 tracking-wider uppercase">Years of Experience</p>
                                </div>
                                <div>
                                    <h4 className="font-display font-bold text-3xl text-slate-900 dark:text-white mb-1">5k+</h4>
                                    <p className="text-xs text-slate-400 tracking-wider uppercase">Successful Smiles</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Why Choose Us - Modern Cards */}
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs mb-4 block">
                            Our Standards
                        </span>
                        <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-6">
                            Why Kottawa Trusts Smile Care
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: "fas fa-award",
                                title: "Proven Expertise",
                                desc: "Led by a senior surgeon with hospital credentials."
                            },
                            {
                                icon: "fas fa-pump-medical",
                                title: "Hospital-Grade Safety",
                                desc: "Plasma Air Ionization for a 100% sterile environment."
                            },
                            {
                                icon: "fas fa-hand-holding-heart",
                                title: "Personalized Care",
                                desc: "No rushed visits. We listen to your concerns first."
                            }
                        ].map((item, idx) => (
                            <div key={idx} className="p-8 bg-white dark:bg-slate-800 rounded-2xl hover:shadow-xl transition-all duration-300 group border border-transparent hover:border-slate-100 dark:hover:border-slate-700">
                                <div className="w-12 h-12 bg-primary/5 rounded-xl flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform">
                                    <i className={`${item.icon} text-xl`}></i>
                                </div>
                                <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-3 group-hover:text-primary transition-colors">{item.title}</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-light">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
            <FloatingWhatsApp />
        </div>
    );
};

export default AboutPage;
